import { StrictMode, Suspense } from "react";
import { createRoot, type Root } from "react-dom/client";
import { ConvexProvider } from "convex/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider, createRouter } from "@tanstack/react-router";

// Import CSS
import "@shared/index.css";

// Import i18n (trebuie importat înainte de componente)
import "@shared/lib/i18n";

// Import client Convex
import { convex } from "@shared/lib/convex";

// Import route tree generat
import { routeTree } from "./routeTree.gen";

// scrollRestoration: comportamentul implicit e exact ce vrem — back/forward te
// duce unde erai, un link nou pornește din capul paginii.
//
// Singurul lucru de reglat e *cum* sare. Routerul cheamă window.scrollTo fără
// `behavior`, deci se aplică `scroll-behavior: smooth` din CSS: de la finalul
// unei pagini de 8000px, saltul la 0 se animă câteva secunde, iar pagina nouă
// se vede întâi de jos. „instant" trece peste regula CSS doar la schimbarea de
// rută; ancorele din pagină (Intră în club, formularul de partener) rămân line.
const router = createRouter({
  routeTree,
  scrollRestoration: true,
  scrollRestorationBehavior: "instant",
});

// Declarare tipuri pentru router
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

/**
 * Recuperare după un deploy, pentru un vizitator rămas cu pagina veche.
 *
 * Rutele sunt împărțite în chunkuri încărcate la navigare. După un deploy,
 * fișierele vechi dispar de pe server: un vizitator care avea deja pagina
 * deschisă cere un chunk care nu mai există, importul dinamic eșuează, iar
 * navigarea se oprește cu o eroare fără sens pentru el.
 *
 * Vite emite `vite:preloadError` exact în situația asta. Reîncărcăm o singură
 * dată — noua pagină aduce hash-urile curente. Ne notăm în sessionStorage ca
 * să nu intrăm în buclă dacă eșecul are altă cauză (de exemplu, lipsa rețelei).
 */
const CHEIE_REINCARCARE = "reincarcare-dupa-deploy";
const PAUZA_INTRE_REINCARCARI_MS = 15_000;

window.addEventListener("vite:preloadError", (event) => {
  // Marcaj temporal, nu un simplu steag: un steag șters la `load` s-ar reseta
  // chiar de reîncărcarea pe care tocmai am declanșat-o, iar dacă eșecul
  // persistă (rețea căzută, nu deploy nou) pagina ar intra în buclă.
  const ultima = Number(sessionStorage.getItem(CHEIE_REINCARCARE) || 0);
  if (Date.now() - ultima < PAUZA_INTRE_REINCARCARI_MS) return;

  event.preventDefault();
  sessionStorage.setItem(CHEIE_REINCARCARE, String(Date.now()));
  window.location.reload();
});

// Creare Query Client pentru TanStack Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minute
      retry: 1,
    },
  },
});

// Componenta de încărcare
function LoadingFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  );
}

// Componenta principală App (fără PostHogProvider static)
function App() {
  return (
    <Suspense fallback={<LoadingFallback />}>
      <ConvexProvider client={convex}>
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router} />
        </QueryClientProvider>
      </ConvexProvider>
    </Suspense>
  );
}

// Render
//
// Montarea trebuie să fie idempotentă: în dev, orice hot update de CSS
// re-execută acest modul (importă stylesheet-ul global). Un al doilea
// createRoot pe același container arunca eroare, Vite invalida modulul și
// făcea reload complet — de unde bucla de refresh în timpul dezvoltării.
// Ținem rootul pe element, deci re-execuția devine un simplu re-render.
const container = document.getElementById("root")! as HTMLElement & {
  _reactRoot?: Root;
};

const root = (container._reactRoot ??= createRoot(container));

root.render(
  <StrictMode>
    <App />
  </StrictMode>
);

// Acest modul importă stylesheet-ul global, dar nu exportă componente, deci
// Fast Refresh nu-l poate actualiza și Vite îl invalida — adică reload complet
// la fiecare regenerare de CSS. Plugin-ul de router rescrie periodic
// routeTree.gen.ts, Tailwind rescanează sursele și emite un update de CSS, iar
// reload-ul declanșa din nou ciclul: pagina se reîmprospăta la nesfârșit.
// Cu montarea idempotentă de mai sus, re-execuția modulului e inofensivă, deci
// îl marcăm self-accepting și oprim bucla.
import.meta.hot?.accept();
